import { createFileRoute } from '@tanstack/react-router';
import { useNavigate } from '@tanstack/react-router';
import { useCleanerRegistrationStore } from '@/stores/cleaner-registration-store';
import { STEPS } from '@/features/cleaner/apply-cleaner/constant';
import { useEffect } from 'react';
import { useGetCleanerCheckQuery } from '@/hooks/queries/use-get-cleaner-check.query';
import { useAuth } from '@/context/auth-context';
import { isPersonalInfoStepDisabledWithOverride } from '@/featureFlag/disablePersonalInfoStep';
import { useUserDetailStore } from '@/stores/user-detail-store';
import { userOnboardingCompletionStore } from '@/stores/user-onboarding-completion';
import axios from '@/lib/axios';
import {
  computeOnboardingCurrentStep,
  isApplicationCompletionFullyComplete,
} from '@/utils/application-completion';

export const Route = createFileRoute('/(cleaner)/apply-cleaner/')({
  component: ApplyCleanerRedirect,
});

function ApplyCleanerRedirect() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const user = useUserDetailStore(s => s.userDetails);
  const applicationCompletion = userOnboardingCompletionStore(
    s => s.applicationCompletion
  );
  const { getFirstIncompleteStep, setIsSignedUp, markStepCompleted } =
    useCleanerRegistrationStore();

  // Check cleaner status from backend to get accurate current stage
  const { data: cleanerCheckData, isLoading: isCheckingCleaner } =
    useGetCleanerCheckQuery({
      enabled: isAuthenticated,
    });

  useEffect(() => {
    // Stale userDetails can remain after logout; never branch on them unless
    // there is an active session (otherwise we navigate to auth-only routes).
    if (!isAuthenticated || !user) {
      return;
    }

    const hasSSN = !!user.ssn;
    const isVerified = user.is_verified;

    if (!hasSSN) {
      return;
    }

    const anyOnboardingIncomplete =
      !applicationCompletion.isCompleteProfile ||
      !applicationCompletion.isCompleteWorksetup ||
      !applicationCompletion.isCompleteCleaningSuppplies ||
      !applicationCompletion.isCompleteAvailabilitySetup ||
      !applicationCompletion.isStripeSetup;

    // redirect to cleaner dashboard if verified and all onboarding complete
    if (hasSSN && isVerified && !anyOnboardingIncomplete) {
      navigate({ to: '/cleaner/dashboard', replace: true });
      return;
    }

    // redirect to /onboarding if (SSN not null and is_verified false)
    if (hasSSN && !isVerified) {
      navigate({ to: '/application-submitted', replace: true });
      return;
    }

    // or any value on onboarding store is false
    if (anyOnboardingIncomplete) {
      navigate({ to: '/onboarding', replace: true });
    }
  }, [isAuthenticated, user, applicationCompletion, navigate]);

  useEffect(() => {
    // Guests must start at signup. Do not use persisted registration progress to
    // jump to personal-info / background-check / completed — those routes use
    // requireAuth and would send anonymous users to /cleaner/login.
    if (!isAuthenticated) {
      navigate({ to: '/apply-cleaner/signup', replace: true });
      return;
    }

    // Wait for cleaner check data to load
    if (isCheckingCleaner) {
      return;
    }

    // Sync store state with backend
    const currentStage = cleanerCheckData?.data?.current_stage || 0;
    const stage3Completed = cleanerCheckData?.data?.stage3_completed || false;

    // Update store state based on backend data
    if (currentStage > STEPS.SIGNUP_CLEANER) {
      setIsSignedUp(true);
      markStepCompleted(STEPS.SIGNUP_CLEANER);

      // If personal info is disabled, mark it as completed too
      if (isPersonalInfoStepDisabledWithOverride()) {
        markStepCompleted(STEPS.PERSONAL_INFO);
      }
    }

    if (
      currentStage > STEPS.PERSONAL_INFO ||
      (isPersonalInfoStepDisabledWithOverride() &&
        currentStage > STEPS.SIGNUP_CLEANER)
    ) {
      markStepCompleted(STEPS.PERSONAL_INFO);
    }

    if (stage3Completed || currentStage >= STEPS.COMPLETED) {
      markStepCompleted(STEPS.BACKGROUND_CHECK);
      // User has completed registration, go to appropriate page
      if (stage3Completed) {
        void (async () => {
          try {
            const completionRes = await axios.get(
              '/users/application-completion'
            );
            const completionData = completionRes.data?.data;
            if (completionData) {
              const prev =
                userOnboardingCompletionStore.getState().applicationCompletion
                  .currentStep;
              const currentStep = computeOnboardingCurrentStep(
                completionData,
                prev
              );
              userOnboardingCompletionStore
                .getState()
                .setApplicationCompletion({
                  ...completionData,
                  currentStep,
                });
              if (isApplicationCompletionFullyComplete(completionData)) {
                navigate({ to: '/cleaner/dashboard', replace: true });
                return;
              }
            }
          } catch {
            /* fall through */
          }
          navigate({ to: '/application-submitted', replace: true });
        })();
        return;
      }
    }

    // Get first incomplete step from synced store
    const firstIncompleteStep = getFirstIncompleteStep();

    // Use TanStack Router's navigate for proper navigation
    const redirectTo = () => {
      switch (firstIncompleteStep) {
        case STEPS.SIGNUP_CLEANER:
          return navigate({ to: '/apply-cleaner/signup', replace: true });
        case STEPS.PERSONAL_INFO:
          return navigate({
            to: '/apply-cleaner/personal-info',
            replace: true,
          });
        case STEPS.BACKGROUND_CHECK:
          return navigate({
            to: '/apply-cleaner/background-check',
            replace: true,
          });
        case STEPS.COMPLETED:
          return navigate({ to: '/apply-cleaner/completed', replace: true });
        default:
          return navigate({ to: '/apply-cleaner/signup', replace: true });
      }
    };

    redirectTo();
  }, [
    isAuthenticated,
    isCheckingCleaner,
    cleanerCheckData,
    getFirstIncompleteStep,
    navigate,
    setIsSignedUp,
    markStepCompleted,
  ]);

  // Guests redirect to signup in an effect; authenticated users wait for /cleaners/check
  if (!isAuthenticated) {
    return null;
  }
  if (isCheckingCleaner) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // This should not be visible as we redirect immediately
  return null;
}
